import merge from "lodash/merge";
import { useContext, useReducer, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API_RETRY_DELAY, API_RETRY_MAX, DEFAULT_ERROR_MESSAGE } from "~/constants/config/common";
import { PAGE_ROUTES } from "~/constants/pages";
import { apiErrorTest, apiErrorTracking } from "~/helpers/apiErrorTest";
import { get_api_base } from "~/helpers/api_base";
import { getHeaders } from "~/helpers/fetch";
import isSessionExpirationError from "~/helpers/sessionExpirationError";
import { checkSessionBlocked } from "~/helpers/sessionBlocking";
import { trackEvent } from "~/helpers/tracking";
import { FuelContext } from "~/providers/FuelProvider";
import { removeQuote, setPageLoading, setSessionExpired } from "~/redux/actions/common";
import { setNotifications } from "~/redux/actions/global";
import { ReduxState } from "~/types/redux";

interface State<T> {
  data?: T;
  error?: T | Error;
  loading: boolean;
}

export interface UseFetch<T> {
  data?: T;
  error?: T | Error;
  loading: boolean;
  fetch: (options?: RequestInit) => Promise<T>;
  abortRequest: () => void;
}

type Action<T> = { type: "loading" } | { type: "success"; payload: T } | { type: "error"; payload: T | Error };

function useFetch<T = unknown>(
  slug?: string,
  options?: RequestInit,
  disableLoader?: boolean,
  disableError?: boolean,
  disableMainLoader?: boolean
): UseFetch<T> {
  const controllerRef = useRef<AbortController>();
  const { router } = useContext(FuelContext);
  const dispatch = useDispatch();
  const retryCount = useRef(0);
  const { tracking, singleForm } = useSelector((store: ReduxState) => store);
  const defaultOptions = { headers: getHeaders(tracking, options) };
  const creditBypassAttempt = singleForm?.creditBypassAttempt | 0;
  const isBypassError =
    singleForm?.enabled === true && creditBypassAttempt <= 2 && singleForm?.contactCredit !== "complete";

  // const [currentRoute, setCurrentRoute] = useState<string>(router?.asPath || '');
  const initialState: State<T> = {
    error: undefined,
    data: undefined,
    loading: false,
  };

  const abortRequest = () => {
    controllerRef.current?.abort();
  };

  const fetchReducer = (state: State<T>, action: Action<T>): State<T> => {
    switch (action.type) {
      case "loading": {
        return { ...initialState, loading: true };
      }
      case "success": {
        return { ...initialState, loading: false, data: action.payload };
      }
      case "error": {
        return { ...initialState, loading: false, error: action.payload };
      }
      default: {
        return state;
      }
    }
  };

  const [state, dispatchState] = useReducer(fetchReducer, initialState);

  const fetchData = async (opts?: RequestInit): Promise<T> => {
    // setCurrentRoute(router?.asPath || '');
    if (!disableLoader) dispatch(setPageLoading(true));
    // const fetchData = async (opts?: CartExperienceRequestOptions): Promise<T> => {
    //   setCurrentRoute(router?.asPath || "");
    //   if (!disableLoader) dispatch(setPageLoading(true));
    dispatchState({ type: "loading" });
    controllerRef.current = new AbortController();
    const retryHeaders = { headers: { retryAttempts: retryCount?.current } };
    try {
      // Check if session is blocked before making request
      if (checkSessionBlocked(tracking?.sessionId)) {
        handleOrchestrationError({}, router, !disableError);
        return;
      }

      console.log(`${get_api_base()}${slug}`);
      // starting merge with empty object since it merges and updates to first argument
      const response = await fetch(
        `${get_api_base()}${slug}${apiErrorTest("?")}`,
        merge({}, defaultOptions, retryHeaders, options, opts, { signal: controllerRef.current?.signal })
      );
      const data = await response?.json();
      if (response.ok) {
        if (!data?.success) {
          handleOrchestrationError(data, router, !disableError);
        } else {
          dispatchState({ type: "success", payload: data as T });
        }
        apiErrorTracking({ url: response?.url, retryCount: retryCount?.current, success: data?.success });
        retryCount.current = retryCount.current + 1;
        return data;
      } else {
        const isSessionExpired = isSessionExpirationError(data?.frapiErrorCode);
        if (retryCount.current < API_RETRY_MAX && data?.isHardStop && !isSessionExpired) {
          const delay = Math.pow(2, retryCount.current) * API_RETRY_DELAY; // Exponential backoff delay
          await new Promise((resolve) => setTimeout(resolve, delay));

          const retryOpts = JSON.parse(opts.body as string);
          opts.body = JSON.stringify(retryOpts);

          apiErrorTracking({ url: response?.url, retryCount: retryCount?.current, success: false });
          retryCount.current = retryCount.current + 1;
          return fetchData(opts);
        } else {
          if (!disableMainLoader) dispatch(setPageLoading(false));
          if (!data?.success) {
            handleOrchestrationError(data, router, !disableError);
          }
        }
        if (!data?.errorMessage) {
          const err = new Error(
            `Partner API Failure: status ${response.status} - ${response.url}, no error message found.`
          );
          trackEvent({
            action: "clientErrored",
            data: {
              errorMessage: err?.message,
              errorClass: err?.name,
            },
          });
        } else {
          const err = new Error(data?.errorMessage);
          trackEvent({
            action: "clientErrored",
            data: {
              errorMessage: err?.message,
              errorClass: err?.name,
            },
          });
        }
        retryCount.current = 0;
      }
    } catch (error) {
      console.log(error); // Log for LogRocket debugging
      if (!disableMainLoader) dispatch(setPageLoading(false));
      dispatch(setNotifications([{ type: "error", message: DEFAULT_ERROR_MESSAGE }]));

      trackEvent({
        action: "clientErrored",
        data: {
          errorMessage: error?.message,
          errorClass: error?.name,
        },
      });
      retryCount.current = 0;

      const isSessionExpired = isSessionExpirationError(error?.frapiErrorCode);
      if (isSessionExpired) {
        dispatch(setSessionExpired(true));
      }
    }
  };

  const handleOrchestrationError = (data, router, shouldShowError = true) => {
    if (!disableLoader) dispatch(setPageLoading(false));
    dispatchState({ type: "error", payload: data as T });

    if (
      (isBypassError || data.userErrorMessage === "credit-bypass") &&
      data?.errorMessage !== "RVError: parentAddressSelected_ChildServiceable"
    )
      return;

    const isSessionExpired = isSessionExpirationError(data?.frapiErrorCode);

    if (isSessionExpired) {
      // If the error code is FRAPI-205 we're assuming it's because the user's session has expired
      dispatch(setSessionExpired(true));
    } else if (shouldShowError) {
      if (data?.errorMessage?.includes("verification failed")) return;
      if (isBypassError && data?.errorMessage !== "RVError: parentAddressSelected_ChildServiceable") return;

      dispatch(setNotifications([{ type: "error", message: data?.userErrorMessage || DEFAULT_ERROR_MESSAGE }]));
    }

    if (data?.errorMessage) {
      if (data.errorMessage.includes("Credit failed with a score of") && data.userErrorMessage !== "credit-bypass") {
        if (isBypassError) return;
        if (data.frapiErrorCode === "FRAPI-307") {
          router?.push(PAGE_ROUTES.EXISTING_CUSTOMER);
        }
      } else {
        trackEvent({
          action: "clientErrored",
          data: {
            errorMessage: data?.errorMessage,
            errorClass: data?.errorName,
          },
        });
      }

      if (data.errorMessage.includes("Quote Status:")) {
        dispatch(removeQuote());
        router?.push(PAGE_ROUTES.ADDRESS_CHECK);
      }
    }
  };

  return {
    ...state,
    fetch: fetchData,
    abortRequest,
  };
}

export default useFetch;
